import api from './axios';

export interface AIKnowledgeDocument {
  id: number;
  partner_id: number | null;
  title: string;
  original_filename: string;
  stored_filename: string;
  file_type: string;
  file_size: number;
  extracted_text: string | null;
  chunks_count: number;
  status: 'pending' | 'processing' | 'indexed' | 'error';
  error_message: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface AIKnowledgeText {
  id: number;
  partner_id: number | null;
  title: string;
  content: string;
  category: string | null;
  chunks_count: number;
  status: 'pending' | 'indexed' | 'error';
  error_message: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface AISystemPrompt {
  id: number;
  partner_id: number | null;
  prompt_text: string;
  is_active: number;
  version: number;
  created_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface KBStats {
  totalDocuments: number;
  totalTextEntries: number;
  totalChunksInDB: number;
  totalChunksInChroma: number;
  documentsByType: Record<string, number>;
  documentsByStatus: Record<string, number>;
  textsByStatus: Record<string, number>;
  totalFileSizeMB: number;
  lastIndexedAt: string | null;
  knowledgeDocChunks: number;
  knowledgeTextChunks: number;
  propertyChunks: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const aiKnowledgeApi = {
  // Documents
  getDocuments: (params?: { page?: number; limit?: number; status?: string; search?: string }) =>
    api.get<{ success: boolean; data: PaginatedResponse<AIKnowledgeDocument> }>('/ai-knowledge/documents', { params }),

  getDocument: (id: number) =>
    api.get<{ success: boolean; data: AIKnowledgeDocument }>(`/ai-knowledge/documents/${id}`),

  uploadDocument: (formData: FormData, onProgress?: (percent: number) => void) =>
    api.post<{ success: boolean; data: AIKnowledgeDocument; message: string }>('/ai-knowledge/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        }
      }
    }),

  updateDocument: (id: number, data: { title: string }) =>
    api.put<{ success: boolean; message: string }>(`/ai-knowledge/documents/${id}`, data),

  deleteDocument: (id: number) =>
    api.delete<{ success: boolean; message: string }>(`/ai-knowledge/documents/${id}`),

  reindexDocument: (id: number) =>
    api.post<{ success: boolean; message: string }>(`/ai-knowledge/documents/${id}/reindex`),

  // Text entries
  getTexts: (params?: { page?: number; limit?: number; category?: string; search?: string }) =>
    api.get<{ success: boolean; data: PaginatedResponse<AIKnowledgeText> }>('/ai-knowledge/texts', { params }),

  getText: (id: number) =>
    api.get<{ success: boolean; data: AIKnowledgeText }>(`/ai-knowledge/texts/${id}`),

  createText: (data: { title: string; content: string; category?: string }) =>
    api.post<{ success: boolean; data: AIKnowledgeText; message: string }>('/ai-knowledge/texts', data),

  updateText: (id: number, data: { title: string; content: string; category?: string }) =>
    api.put<{ success: boolean; message: string }>(`/ai-knowledge/texts/${id}`, data),

  deleteText: (id: number) =>
    api.delete<{ success: boolean; message: string }>(`/ai-knowledge/texts/${id}`),

  // System prompt
  getSystemPrompt: () =>
    api.get<{ success: boolean; data: AISystemPrompt | null }>('/ai-knowledge/system-prompt'),

  updateSystemPrompt: (data: { prompt_text: string }) =>
    api.put<{ success: boolean; data: AISystemPrompt; message: string }>('/ai-knowledge/system-prompt', data),

  getPromptHistory: () =>
    api.get<{ success: boolean; data: AISystemPrompt[] }>('/ai-knowledge/system-prompt/history'),

  // Stats
  getStats: () =>
    api.get<{ success: boolean; data: KBStats }>('/ai-knowledge/stats'),

  reindexAll: () =>
    api.post<{ success: boolean; message: string; data: { documents: number; textEntries: number } }>('/ai-knowledge/reindex-all'),
};
